import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useMarketData } from './MarketDataContext';
import { useBybitData } from './BybitDataContext';
import { currencyOptions, formatDisplayAmount, getDisplayRate, isDisplayCurrency, type DisplayCurrency } from '../utils/displayCurrency';

interface DisplayCurrencyContextValue {
  currency: DisplayCurrency;
  setCurrency: (currency: DisplayCurrency) => Promise<void>;
  formatAmount: (amount: number) => string;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const DisplayCurrencyContext = createContext<DisplayCurrencyContextValue | undefined>(undefined);

export function DisplayCurrencyProvider({ userId, children }: { userId?: string; children: ReactNode }) {
  const [currency, setSelectedCurrency] = useState<DisplayCurrency>('USD');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const saveInProgress = useRef(false);
  const { getPriceBySymbol, getSnapshotPriceBySymbol } = useMarketData();
  const { getPriceBySymbol: getCryptoPrice } = useBybitData();

  useEffect(() => {
    const current = ++generation.current;
    setSelectedCurrency('USD');
    setError(null);
    setLoading(true);
    setSaving(false);
    saveInProgress.current = false;
    if (!userId) {
      setLoading(false);
      return;
    }
    async function loadPreference() {
      try {
        const { data, error: loadError } = await supabase.from('users').select('display_currency').eq('id', userId!).single();
        if (current !== generation.current) return;
        if (loadError) throw loadError;
        if (isDisplayCurrency(data.display_currency)) setSelectedCurrency(data.display_currency);
      } catch {
        if (current === generation.current) setError('Could not load your saved currency. Please reload to try again.');
      } finally {
        if (current === generation.current) setLoading(false);
      }
    }
    void loadPreference();
    return () => { generation.current = current + 1; };
  }, [userId]);

  async function setCurrency(next: DisplayCurrency) {
    if (!userId || loading || saveInProgress.current || !isDisplayCurrency(next)) return;
    const current = generation.current;
    const previous = currency;
    saveInProgress.current = true;
    setSaving(true);
    setError(null);
    setSelectedCurrency(next);
    try {
      const { error: saveError } = await supabase.from('users').update({ display_currency: next }).eq('id', userId).select('id').single();
      if (saveError) throw saveError;
    } catch {
      if (current === generation.current) {
        setSelectedCurrency(previous);
        setError('Could not save your currency. Please try again.');
      }
    } finally {
      if (current === generation.current) {
        saveInProgress.current = false;
        setSaving(false);
      }
    }
  }

  const rate = getDisplayRate(currency, symbol => getPriceBySymbol(symbol) || getSnapshotPriceBySymbol(symbol), getCryptoPrice);
  const formatAmount = (amount: number) => loading ? '—' : formatDisplayAmount(amount, currency, rate);
  return <DisplayCurrencyContext.Provider value={{ currency, setCurrency, formatAmount, loading, saving, error }}>{children}</DisplayCurrencyContext.Provider>;
}

export function useDisplayCurrency() {
  const context = useContext(DisplayCurrencyContext);
  if (!context) throw new Error('useDisplayCurrency requires DisplayCurrencyProvider');
  return context;
}

export function CurrencySelector() {
  const { currency, setCurrency, loading, saving, error } = useDisplayCurrency();
  return <div className="shrink-0">
    <select aria-label="Portfolio display currency" value={currency} disabled={loading || saving}
      onChange={event => { if (isDisplayCurrency(event.target.value)) void setCurrency(event.target.value); }}
      className="block h-6 w-14 bg-slate-700 text-white rounded-md px-1 text-[11px] disabled:opacity-50">
      {currencyOptions.map(option => <option key={option.code} value={option.code}>{option.code}</option>)}
    </select>
    {error && <p role="alert" className="text-red-400 text-xs mt-1 max-w-48">{error}</p>}
  </div>;
}
